import { useMemo, useState } from "react";
import { genres, works } from "@/mocks/fengchiData";

export default function WorkRanking() {
  const [activeGenre, setActiveGenre] = useState("全部");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const list = useMemo(() => {
    const filtered =
      activeGenre === "全部" ? works : works.filter((w) => w.genre === activeGenre);
    return [...filtered].sort((a, b) => b.score - a.score);
  }, [activeGenre]);

  return (
    <section id="works" className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-accent-600 text-sm font-medium">
            <i className="ri-bar-chart-2-line" />
            作品口碑榜
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            按题材筛选，找到你想看的那一部
          </h2>
          <p className="mt-4 text-foreground-700 text-sm md:text-base leading-relaxed">
            下面的作品按综合评分从高到低排列。点击题材标签可以快速筛选，
            每张卡片都能展开看到更完整的简介——包括它在观众口中到底好在哪里、又常被吐槽什么。
          </p>
        </div>

        <div className="mt-6 md:mt-8 flex flex-wrap gap-2">
          {genres.map((genre) => {
            const active = genre === activeGenre;
            return (
              <button
                key={genre}
                type="button"
                onClick={() => setActiveGenre(genre)}
                aria-pressed={active}
                className={`px-4 py-2 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                }`}
              >
                {genre}
              </button>
            );
          })}
        </div>

        <p className="mt-4 text-xs md:text-sm text-foreground-500">
          共 {list.length} 部作品
          {activeGenre !== "全部" && ` · 题材：${activeGenre}`}
        </p>

        {list.length === 0 ? (
          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
            <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-inbox-line text-2xl" />
            </span>
            <p className="mt-3 text-foreground-600 text-sm">该题材下暂时还没有收录作品。</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {list.map((work) => {
              const expanded = expandedId === work.id;
              return (
                <article
                  key={work.id}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-100 overflow-hidden"
                >
                  <div className="relative w-full aspect-[3/4] bg-background-200">
                    <img
                      src={work.poster}
                      alt={`《${work.title}》海报，${work.genre}题材`}
                      title={`风驰影院 ${work.title} 评分 ${work.score}`}
                      className="w-full h-full object-cover object-top"
                    />
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-sm font-bold">
                      <i className="ri-star-fill" />
                      {work.score}
                    </span>
                    <span className="absolute top-3 right-3 px-2 py-1 rounded-md bg-background-50/90 text-foreground-800 text-xs">
                      {work.year}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1 p-4">
                    <h3 className="font-heading text-lg font-bold text-foreground-950">
                      {work.title}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {work.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] md:text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                      {work.oneLine}
                    </p>

                    {expanded && (
                      <div className="mt-3 space-y-2 animate-fadeUp">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {work.synopsis}
                        </p>
                        <p className="text-sm text-primary-700 bg-primary-50 rounded-md px-3 py-2">
                          <i className="ri-emotion-line mr-1" />
                          {work.highlight}
                        </p>
                      </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
                      <span>导演 {work.director}</span>
                      <span>{work.duration}</span>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-foreground-500">
                        {work.ratingCount}人评分
                      </span>
                      <button
                        type="button"
                        onClick={() => setExpandedId(expanded ? null : work.id)}
                        aria-expanded={expanded}
                        className="inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
                      >
                        {expanded ? "收起简介" : "展开简介"}
                        <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}